import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

export default function PrivacyPolicyPage() {
    return (
        <div className="min-h-screen bg-[#FDFEFF]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-navy-50/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Logo className="w-9 h-9" textClassName="text-xl" />
                    </Link>
                    <Link to="/" className="flex items-center gap-2 text-navy-700 font-medium hover:text-sky-500 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <main className="pt-32 pb-20 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-12">
                        <span className="inline-flex items-center px-4 py-1.5 bg-sky-50 text-sky-600 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-sky-100">
                            Legal
                        </span>
                        <h1 className="text-4xl sm:text-5xl font-heading font-bold text-navy-900 mb-4">
                            Privacy Policy
                        </h1>
                        <p className="text-navy-500 text-sm">
                            Last updated: March 1, 2026
                        </p>
                    </div>

                    <div className="prose prose-navy max-w-none space-y-10">
                        {/* Introduction */}
                        <section>
                            <p className="text-navy-600 leading-relaxed text-lg">
                                At EstateCloud, we are committed to safeguarding your privacy. This Privacy Policy describes how we collect, use, disclose, and protect your personal information when you use our platform and services.
                            </p>
                        </section>

                        {/* Section 1 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                1. Information We Collect
                            </h2>

                            <h3 className="text-lg font-semibold text-navy-800 mb-3 mt-6">1.1 Personal Information</h3>
                            <p className="text-navy-600 leading-relaxed mb-3">
                                When you register or use our Platform, we may collect:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>Full name, email address, and phone number.</li>
                                <li>Agency or company name, RERA registration number, and business details.</li>
                                <li>Billing address and payment information (processed securely through third-party payment providers).</li>
                                <li>Profile photos and any additional information you voluntarily share.</li>
                            </ul>

                            <h3 className="text-lg font-semibold text-navy-800 mb-3 mt-6">1.2 Property Data</h3>
                            <p className="text-navy-600 leading-relaxed mb-3">
                                Information you submit about properties, including:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>Property descriptions, specifications, pricing, and location details.</li>
                                <li>Images, floor plans, and multimedia files associated with listings.</li>
                                <li>RERA registration details for individual properties.</li>
                            </ul>

                            <h3 className="text-lg font-semibold text-navy-800 mb-3 mt-6">1.3 Usage Data</h3>
                            <p className="text-navy-600 leading-relaxed mb-3">
                                We automatically collect information about how you interact with our Platform:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>IP address, browser type, operating system, and device information.</li>
                                <li>Pages visited, features used, and time spent on the Platform.</li>
                                <li>Referral URLs and search queries within the Platform.</li>
                            </ul>
                        </section>

                        {/* Section 2 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                2. How We Use Your Information
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                We use the information we collect for the following purposes:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li><strong>Provide and maintain the Platform:</strong> To create accounts, display property listings, process enquiries, and deliver our core services.</li>
                                <li><strong>Process payments:</strong> To manage subscriptions, billing, and transaction-related communications.</li>
                                <li><strong>Improve user experience:</strong> To analyze usage patterns, troubleshoot issues, and develop new features.</li>
                                <li><strong>Communication:</strong> To send service-related notifications, updates, customer support responses, and promotional materials (with your consent).</li>
                                <li><strong>Security and fraud prevention:</strong> To detect, prevent, and address technical issues, security threats, and fraudulent activity.</li>
                                <li><strong>Legal compliance:</strong> To comply with applicable laws, regulations, and legal processes, including RERA requirements.</li>
                            </ul>
                        </section>

                        {/* Section 3 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                3. Data Sharing & Third Parties
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                We do not sell your personal information. We may share your data only in the following circumstances:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li><strong>Service providers:</strong> With trusted third-party service providers who assist in operating the platform (e.g., payment processors, hosting providers, email services).</li>
                                <li><strong>Public listings:</strong> Property information submitted by agencies is displayed publicly on the platform and agency websites as intended by the service.</li>
                                <li><strong>Legal requirements:</strong> When required by applicable law, court order, or governmental regulation.</li>
                                <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, in which case your information may be transferred as part of the business assets.</li>
                                <li><strong>With your consent:</strong> In any other circumstances where you have provided your explicit consent for data sharing.</li>
                            </ul>
                        </section>

                        {/* Section 4 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                4. Data Security
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                We implement industry-standard security measures to protect your personal information, including encryption of data in transit and at rest, secure server infrastructure, regular security assessments, and access controls. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials.
                            </p>
                        </section>

                        {/* Section 5 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                5. Data Retention
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                We retain your personal information for as long as your account is active or as needed to provide you with our services. We may also retain data as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements. When your data is no longer needed, we will securely delete or anonymize it within a reasonable timeframe.
                            </p>
                        </section>

                        {/* Section 6 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                6. Your Rights
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                Depending on your location and applicable law, you may have the following rights regarding your personal data:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                                <li><strong>Correction:</strong> Request correction of any inaccurate or incomplete data.</li>
                                <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal retention requirements.</li>
                                <li><strong>Portability:</strong> Request your data in a structured, commonly used, and machine-readable format.</li>
                                <li><strong>Objection:</strong> Object to the processing of your personal data for specific purposes.</li>
                                <li><strong>Withdraw consent:</strong> Withdraw any previously given consent for data processing at any time.</li>
                            </ul>
                            <p className="text-navy-600 leading-relaxed mt-4">
                                To exercise any of these rights, please contact us at the information provided below. We will respond to your request within 30 days.
                            </p>
                        </section>

                        {/* Section 7 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                7. Cookies & Tracking Technologies
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                We use cookies and similar tracking technologies to enhance your experience on the Platform:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li><strong>Essential cookies:</strong> Required for basic functionality such as authentication and security.</li>
                                <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with the Platform to improve our services.</li>
                                <li><strong>Preference cookies:</strong> Store your settings and preferences for future visits.</li>
                            </ul>
                            <p className="text-navy-600 leading-relaxed mt-4">
                                You can manage your cookie preferences through your browser settings. Please note that disabling certain cookies may affect the functionality of the Platform.
                            </p>
                        </section>

                        {/* Section 8 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                8. Third-Party Links
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                Our Platform may contain links to third-party websites or services that are not operated by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party sites or services. We strongly advise you to review the privacy policy of every site you visit.
                            </p>
                        </section>

                        {/* Section 9 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                9. Children's Privacy
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                EstateCloud is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child without parental consent, we will take steps to delete that information promptly.
                            </p>
                        </section>

                        {/* Section 10 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                10. Changes to This Policy
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. We will notify you of any significant changes by posting the updated policy on the Platform and updating the "Last updated" date. Your continued use of the Platform after any changes constitutes your acceptance of the updated Privacy Policy.
                            </p>
                        </section>

                        {/* Section 11 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                11. Contact Us
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
                            </p>
                            <div className="mt-4 p-6 bg-navy-50/50 rounded-2xl border border-navy-100">
                                <p className="font-semibold text-navy-900 mb-1">EstateCloud</p>
                                <p className="text-navy-600">Email: privacy@estatecloud.in</p>
                                <p className="text-navy-600">Hyderabad, Telangana, India</p>
                            </div>
                        </section>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-6 bg-navy-950">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-sky-500" />
                            <span className="text-sm font-heading font-bold text-white">EstateCloud</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-navy-400">
                            <Link to="/terms" className="hover:text-sky-400 transition-colors">Terms & Conditions</Link>
                            <span>© 2026 EstateCloud. All rights reserved.</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
